import React, { useContext, useEffect } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider, useDataModel } from '@thd-nucleus/data-sources';
import {
  RequestAQuoteCta,
  ScheduleAConsultationCta
} from '@thd-olt-component-react/home-services-cta-buttons';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { dualPathIdentifiers } from '../utils';
import { dataModel } from './dataModel';
import { leadGenFormTitle } from '../utils/lead-generation-constants';
import { getCostGuideURL } from './cost-guide-urls-map';
import { WhatToExpect } from '../common/what-to-expect/WhatToExpect';
import { serviceIdentifierToProgramCatogoryMap } from './review-urls-map';
import { ServiceReview } from '../common/service-reviews/ServiceReview';

const { string } = proptypes;

const LeadGeneration = ({ itemId }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('home-services.ready'); }, []);

  const { isConsumerApp } = useContext(ExperienceContext);
  const opts = {
    skip: !itemId,
    variables: {
      itemId
    },
    ssr: true,
  };
  const { data } = useDataModel('product', opts);

  if (!itemId || !data?.product?.details?.installation) {
    return null;
  }

  const { programCategory, salientPoints } = data.product.details.installation;

  const costGuideUrl = getCostGuideURL(programCategory);
  const serviceIdentifier = serviceIdentifierToProgramCatogoryMap.get(programCategory);

  const isSAC = ['KITCHEN_CABINETS', 'BATHLINERS', 'SHOWER_DOOR'].includes(dualPathIdentifiers[programCategory]);
  const headerDescription = leadGenFormTitle[programCategory];

  const target = isConsumerApp ? '_self' : '_blank';

  return (
    <div data-component="LeadGeneration">
      <Typography variant="body-xs" weight="bold" color="brand">
        Leave Your Next Project to Us
      </Typography>
      <Typography
        variant="h3"
        weight="bold"
      >
        What To Expect
      </Typography>
      <section className="sui-pt-2 sui-pb-2">
        <Typography variant="body-xs" color="subtle"> Get a quote during a free consultation</Typography>
      </section>
      <WhatToExpect whatToExpectList={salientPoints} />
      {costGuideUrl
        && (
          <div className="sui-mt-2">
            <Typography variant="body-xs" color="subtle">
              Need help understanding cost?&nbsp;
              <span>
                <Link
                  target={target}
                  href={costGuideUrl}
                  variant="body-xs"
                  data-testid="cost-guide-link"
                >
                  Use Our Cost Guide to Help Estimate Your Project Cost
                </Link>
              </span>
            </Typography>
          </div>
        )}
      <div className="sui-pb-8 sui-pt-4">
        {isSAC
          ? (
            <ScheduleAConsultationCta
              itemId={itemId}
              headerDescription={headerDescription}
            />
          )
          : (
            <QueryProvider cacheKey="request-a-quote-cta" dataSource="catalog">
              <RequestAQuoteCta
                itemId={itemId}
                headerDescription={headerDescription}
              />
            </QueryProvider>
          )}
      </div>
      {serviceIdentifier && (
        <ServiceReview serviceIdentifier={serviceIdentifier}>
          <div>
            {isSAC
              ? (
                <QueryProvider cacheKey="schedule-a-consulation-cta">
                  <ScheduleAConsultationCta
                    itemId={itemId}
                    headerDescription={headerDescription}
                    enableNewTab
                  />
                </QueryProvider>
              )
              : (
                <QueryProvider cacheKey="request-a-quote-cta" dataSource="catalog">
                  <RequestAQuoteCta
                    itemId={itemId}
                    enableNewTab
                  />
                </QueryProvider>
              )}
          </div>
        </ServiceReview>
      )}
    </div>
  );
};

LeadGeneration.displayName = 'LeadGeneration';

LeadGeneration.dataModel = dataModel;

LeadGeneration.propTypes = {
  itemId: string.isRequired,
};

export { LeadGeneration };
